<script setup lang="ts">
import type { AppDownloadLinksResponse } from '~/api/download'
import type { FeedbackDetailData } from '~/api/feedback'

type FeedbackRankItem = {
  name?: string
  icon?: string
  code?: string
  alias?: string
}

type AppDownloadItem = {
  name: string
  icon: string
  url: string
}

const props = defineProps<{
  feedbackDetailData?: FeedbackDetailData | null
  downloadLinks?: AppDownloadLinksResponse
}>()

const { t, localePath } = useAppI18n()

const appDownloadItems = computed<AppDownloadItem[]>(() => [
  {
    name: 'App Store',
    icon: 'https://resources1.interface003.com/web30/2026/06/24/538cffcb-c8a3-49bd-8173-c41ceb8ae3fe.png',
    url: props.downloadLinks?.data?.appStoreUrl || '',
  },
  {
    name: 'Google Play',
    icon: 'https://resources1.interface003.com/web30/2026/06/24/81105f73-95a1-4aeb-a7c8-2c7a240d3ba1.png',
    url: props.downloadLinks?.data?.googlePlayUrl || '',
  },
  {
    name: 'Android APK',
    icon: 'https://resources1.interface003.com/web30/2026/06/24/0a654812-0d7c-45b6-896a-f6189c78ca01.png',
    url: props.downloadLinks?.data?.androidApkUrl || '',
  },
])

const route = useRoute()
const { setBreadcrumb } = useLsBreadcrumb()

/** 点击跳转交易商排行详情前，缓存当前页面为来源面包屑 */
const handleExchangeRankingClick = (item: FeedbackRankItem) => {
  setBreadcrumb(item.alias, {
    labelKey: '84077',
    route: route.fullPath,
  })
}

const rankList = computed(() => {
  const detailData = props.feedbackDetailData
  if (detailData?.weekRankings && detailData.weekRankings.length > 0) {
    return detailData.weekRankings
  }
  return detailData?.totalRankings ?? []
})
</script>

<template>
  <div class="flex w-full shrink-0 flex-col space-y-[24px] bg-[#F5F6F8] min-[1248px]:w-[384px]">
    <div
      v-if="rankList && rankList.length > 0"
      class="flex flex-col space-y-[12px] rounded-[12px] border border-[rgba(5,21,36,0.06)] bg-[#FFF] px-[24px] pt-[8px] pb-[16px]"
    >
      <div class="w-full border-b-[0.5px] border-[rgba(5,21,36,0.06)]">
        <div class="flex items-center py-[16px]">
          <div class="text-[20px] leading-[28px] font-[600] whitespace-nowrap text-[#292a2e]">
            {{
              props.feedbackDetailData?.weekRankings && props.feedbackDetailData.weekRankings.length > 0
                ? t('83859')
                : t('84607')
            }}
          </div>
        </div>
      </div>
      <div
        class="flex w-full overflow-hidden max-[1247px]:space-x-[12px] max-[1247px]:overflow-x-auto max-[1247px]:overflow-y-hidden min-[1248px]:flex-col"
      >
        <NuxtLink
          v-for="(item, index) in rankList"
          :key="`${item.name}-${index}`"
          :to="localePath(item.alias ? `/exchanges/${item.alias}` : '/')"
          target="_blank"
          class="flex shrink-0 cursor-pointer items-center space-x-[8px] overflow-hidden rounded-[8px] px-[-8px] py-[12px] transition-colors duration-200 hover:bg-[#f8f8f8] min-[1248px]:w-full"
          @click="handleExchangeRankingClick(item)"
        >
          <div
            class="w-[20px] shrink-0 text-center text-[14px] leading-[22px] font-[400] whitespace-nowrap"
            :class="Number(index) < 3 ? 'text-[#292a2e]' : 'text-[#b7b9be]'"
          >
            {{ Number(index) + 1 }}
          </div>
          <CommonImage class="h-[20px] w-[20px] shrink-0 rounded-[2px] object-cover" :src="item.icon" />
          <div class="min-w-0 truncate text-[16px] leading-[24px] font-[400] whitespace-nowrap text-[#292a2e]">
            {{ item.name }}
          </div>
        </NuxtLink>
      </div>
    </div>
    <div class="flex flex-col space-y-[24px] overflow-hidden rounded-[12px] bg-[#e9f2ff] p-[24px]">
      <div class="flex w-full flex-col space-y-[4px]">
        <div class="text-[16px] leading-[24px] font-[400] text-[#505258]">{{ t('83779') }}</div>
        <div class="text-[20px] leading-[28px] font-[600] text-[#292a2e]">{{ t('82496') }}</div>
      </div>
      <div
        class="flex w-full flex-col space-y-[12px] max-[1247px]:flex-row max-[1247px]:space-y-[0] max-[1247px]:space-x-[12px]"
      >
        <NuxtLink
          v-for="item in appDownloadItems"
          :key="item.name"
          :to="item.url || '/'"
          target="_blank"
          external
          class="flex min-w-0 flex-1 cursor-pointer items-center space-x-[12px] rounded-[8px] bg-[#FFF] px-[16px] py-[8px]"
        >
          <CommonImage class="size-[28px] shrink-0 object-cover" :src="item.icon" />
          <div class="flex min-w-0 flex-1 flex-col text-[#000]">
            <div class="text-[11px] leading-[16px] font-[400] whitespace-nowrap">Download on the</div>
            <div class="truncate text-[16px] leading-[24px] font-[600] whitespace-nowrap">{{ item.name }}</div>
          </div>
          <CommonImage
            class="size-[20px] shrink-0 object-cover"
            src="https://resources1.interface003.com/web30/2026/06/24/5d28c5a1-25e0-45d4-9e3e-d3f3218550a7.png"
          />
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
